import { FC, ReactNode, useState } from 'react'
import Dropdown from './Dropdown'
import { fetchComparableMarkets } from '../services/http-client'
import React from 'react'
import { ComparableMarketResponse } from '@libs/types/generated/housing-market-ui'

import { toCurrency, toGeoName, toString } from '../utils/formatter'
import Tooltip from './Tooltip'
import Icon from './Icon'

interface Props {
  geo: string
  coverage: Partial<Coverage>[]
  comparableMarkets: ComparableMarketResponse[]
}

const repeat3 = [0, 1, 2]
const metrics = {
  median_sale_price: { label: 'Median home sale', formatter: toCurrency },
  median_dom: { label: 'Days on market', formatter: toString },
  median_house_payment: {
    label: 'Monthly house payment ',
    formatter: toCurrency
  },
  median_cost_to_buy: {
    label: 'Cost to buy a house',
    formatter: toCurrency
  },
  median_cost_to_sale: {
    label: 'Cost to sell a house',
    formatter: toCurrency
  }
}

const infoIcon = (
  <Icon
    variant="info"
    className="Icon--xs ml-1"
    style={{ marginTop: '-2px' }}
  />
)

const tooltipContent: Record<string, ReactNode | string> = {
  median_cost_to_sale:
    'Cost to sell a house is based on 6% of the median home sale price to estimate the real estate agent commissions in addition to another 1% of the median home sale price to cover seller closing costs.',
  median_cost_to_buy:
    'Cost to buy a house is based on 4% of the median home sale price to estimate the buyer’s closing costs in addition to a 10% down payment.',
  median_house_payment:
    'House payment covers mortgage principal and interest and is based on the median home sale price and mortgage rates. Additionally, estimated local homeowners insurance and property taxes are included.'
}

type Metric = keyof typeof metrics

const CityComparison: FC<Props> = ({ geo, coverage, comparableMarkets }) => {
  const [data, setData] = useState<Partial<ComparableMarketResponse>[]>(
    comparableMarkets.slice(0, 3)
  )

  const cityOptions = [
    { label: '', value: '', disable: true },
    {
      label: 'National',
      value: JSON.stringify({
        city: 'National',
        state_code: 'US',
        region: 'national'
      })
    }
  ].concat(
    coverage.map(({ city, state_code, region_type_name }) => ({
      label: toGeoName(city, state_code),
      value: JSON.stringify({
        city,
        state_code,
        region: region_type_name
      })
    }))
  )

  const changeCity = async (serializedValue: string, index: number) => {
    const { city, state_code, region } = JSON.parse(serializedValue)

    // reset current data to indicate loading
    data[index] = { city: data[index].city, state_code: data[index].state_code }
    setData([...data])

    // fetch market data
    try {
      const marketData = await fetchComparableMarkets({
        city,
        state: state_code,
        region_type: region
      })
      const newData = [...data]
      newData[index] = marketData[0]
      setData(newData)
    } catch (e) {
      console.log(e) // todo
    }
  }

  const toSelectValue = (data: any) => {
    return JSON.stringify({
      city: data.city,
      state_code: data.state_code,
      region: data.region_type
    })
  }

  return (
    <div className="p-4 -mx-4" style={{ overflow: 'auto' }}>
      <table className="Table Table--stripedOdd box-shadow-2">
        <thead>
          <tr>
            <th
              className="w-1/3  text-center"
              style={{ verticalAlign: 'bottom', minWidth: '14em' }}
            >
              <div className="type-heading-four mt-6 mb-4">
                {toGeoName(data[0].city, data[0].state_code)}
              </div>
            </th>
            <th
              className="w-1/3 text-center border-l border-gray-mid"
              style={{ minWidth: '14em' }}
            >
              <Dropdown
                id="city-comparison-dropdown-1"
                options={cityOptions}
                selected={toSelectValue(data[1])}
                className="mx-auto"
                style={{ maxWidth: '16em' }}
                onOptionSelect={serializedValue =>
                  changeCity(serializedValue, 1)
                }
              />
              <div className="type-heading-four mt-6 mb-4">
                {toGeoName(data[1].city, data[1].state_code)}
              </div>
            </th>
            <th
              className="w-1/3 text-center border-l border-gray-mid"
              style={{ minWidth: '14em' }}
            >
              <Dropdown
                id="city-comparison-dropdown-2"
                options={cityOptions}
                selected={toSelectValue(data[2])}
                className="mx-auto"
                style={{ maxWidth: '16em' }}
                onOptionSelect={serializedValue =>
                  changeCity(serializedValue, 2)
                }
              />
              <div className="type-heading-four mt-6 mb-4">
                {toGeoName(data[2].city, data[2].state_code)}
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          {Object.entries(metrics).map(([key, one]) => (
            <React.Fragment key={key}>
              <tr>
                {repeat3.map(i => (
                  <td
                    key={i}
                    className={`text-center ${
                      i > 0 ? 'border-l border-gray-mid' : ''
                    }`}
                  >
                    {one.label}
                    {tooltipContent[key] && (
                      <Tooltip
                        buttonText={infoIcon}
                        className={'leading-none'}
                        underline={false}
                      >
                        <div className="type-body-three text-slate">
                          {tooltipContent[key]}
                        </div>
                      </Tooltip>
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                {repeat3.map(i => (
                  <td
                    key={i}
                    className={`text-center font-bold ${
                      i > 0 ? 'border-l border-gray-mid' : ''
                    }`}
                  >
                    {data[i][key as Metric]
                      ? one.formatter(data[i][key as Metric])
                      : '--'}
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default CityComparison
